import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONFIG } from "@/config";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro:
      "This Privacy Policy explains how Auxieron Pvt. Ltd. collects, uses and protects the information you share with us through this website.",
    sections: [
      ["Information we collect", "When you submit an enquiry, we collect details you voluntarily provide such as your name, mobile number, email, age, income range, intended investment and financial goals. We do not collect this information unless you choose to share it."],
      ["How we use your information", "We use your details solely to understand your requirements and to contact you regarding your enquiry about our financial services. We do not sell or rent your personal information to third parties."],
      ["Data storage & security", "Your information is stored securely and access is restricted to authorised team members. We take reasonable measures to protect it against unauthorised access, alteration or disclosure."],
      ["Communication", "By submitting the form you consent to being contacted by our team via phone, WhatsApp or email in relation to your enquiry."],
      ["Your choices", "You may request access to, correction of, or deletion of your personal information at any time by contacting us."],
      ["Updates", "We may update this policy from time to time. Any changes will be reflected on this page."],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    intro:
      "By accessing and using this website, you agree to the following terms. Please read them carefully.",
    sections: [
      ["Nature of information", "The content on this website is provided for general information and educational purposes only. It does not constitute financial, investment, tax or legal advice, nor a recommendation to buy or sell any product."],
      ["No guaranteed outcomes", "Investments in mutual funds and other market-linked products are subject to market risks. Past performance is not indicative of future results, and no returns are guaranteed."],
      ["Personalised guidance", "Any personalised guidance is provided only after understanding your specific situation. Decisions you take based on such guidance remain your responsibility."],
      ["Intellectual property", "All content, branding and design on this website belong to Auxieron Pvt. Ltd. and may not be reproduced without permission."],
      ["Third-party links", "This website may link to external sites (such as Instagram or WhatsApp). We are not responsible for the content or practices of those third-party platforms."],
      ["Governing law", "These terms are governed by the laws of India, with jurisdiction in Bhubaneswar, Odisha."],
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    intro:
      "Please read the following disclaimer regarding the services and information offered by Auxieron Pvt. Ltd.",
    sections: [
      ["General", "Auxieron Pvt. Ltd. provides financial services and guidance. The information on this website is intended to help you make informed decisions and does not amount to an assurance of any specific result."],
      ["Market risk", "Mutual fund and securities investments are subject to market risks. Please read all scheme-related documents carefully before investing. Insurance is the subject matter of solicitation."],
      ["Fixed income", "Bonds, fixed deposits and REITs carry credit, interest-rate, liquidity and market risks. Yields and returns are not guaranteed, and past coupons or distributions are not indicative of future payouts."],
      ["Loans", "Auxieron Pvt. Ltd. is not a bank, NBFC or lender and does not lend money. Loans are arranged through third-party lending partners. Sanction, interest rates, fees and terms are at the sole discretion of the lending partner, and Auxieron does not guarantee approval of any application."],
      ["Calculators & articles", "Calculators and educational articles on this website are illustrative and for general information only. They rely on assumptions you provide and do not constitute personalised financial, tax or legal advice."],
      ["No performance claims", "We do not claim to be the best, number one, or to provide guaranteed or highest returns. Any figures shown on this site are illustrative and not indicative of actual or future returns."],
      ["Regulatory details", "Auxieron's founders are AMFI-registered Mutual Fund Distributors: Rohan Kumar Sarbadhikari (ARN-354104, BSE Member Code 66455) and Madhurima Das (ARN-354103, BSE Member Code 66403)."],
      ["Independent advice", "You are encouraged to consider your own circumstances and, where appropriate, seek independent professional advice before making financial decisions."],
    ],
  },
};

export const PolicyPage = ({ type }) => {
  const data = CONTENT[type];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  return (
    <div className="bg-lightbg pt-24 sm:pt-28">
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14">
        <Link
          to="/"
          data-testid="policy-back"
          className="inline-flex items-center gap-2 text-sm font-semibold text-navy/70 transition-colors hover:text-navy"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <p className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-royal">Legal</p>
        <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
          {data.title}
        </h1>
        <p className="mt-5 text-base leading-relaxed text-ink/70">{data.intro}</p>

        <div className="mt-10 space-y-8">
          {data.sections.map(([h, body]) => (
            <section key={h}>
              <h2 className="font-display text-xl font-bold text-navy">{h}</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink/70">{body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-navy/[0.08] bg-white p-6">
          <p className="text-sm text-ink/60">
            Questions about this page? Reach us on Instagram{" "}
            <a href={CONFIG.instagramUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-royal">
              {CONFIG.instagramHandle}
            </a>{" "}
            or via <a href={CONFIG.whatsappUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-royal">WhatsApp</a>.
          </p>
          <p className="mt-3 text-xs text-ink/45">
            © {new Date().getFullYear()} {CONFIG.legalName}, {CONFIG.location}.
          </p>
        </div>
      </div>
    </div>
  );
};
